import { FormikContext, useFormik } from "formik";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { FaTimes } from "react-icons/fa";
import animatedScroll from "animated-scroll-to";
import {
  ButtonPrimary,
  ButtonPrimaryTransparent,
  ButtonSecondary,
  DoubleContent,
  Headline,
  InputControl,
  Link,
  Loading,
  ModalError,
  SectionContent,
  SelectControl,
  StatusContemplacao,
  Text,
} from "../../../../components";
import { ResultadoDasAssembleiasForm } from "../../../../forms";
import { useResultadoAssembleia } from "../../../../hooks";
import { applyKey, getMonths } from "../../../../utils";

import * as S from "./ResultadoDasAssembleias.Style";
import PainelDeResultados from "./PainelDeResultados";
import { links } from "../../../../config";
import { ReactComponent as IlustracaoMobile } from "../../../../assets/images/consorcio/ilustracao-contemplacao-resultado-assembleia-mobile.svg";
import { ReactComponent as IlustracaoDesktop } from "../../../../assets/images/consorcio/ilustracao-contemplacao-resultado-assembleia-desktop.svg";
import { ModalContext } from "../../../../contexts";

const getPaddingValue = (str) => {
  const v = Number.parseInt(str?.replace(/\D/, ""), 10);

  return v ? v.toString().padStart(6, "0").substr(0, 6) : "";
};

const InputCotaControl = (props) => {
  const { cota, showRemoveButton, onCotaChange, onRemoveCota } = props;

  const formik = useFormik({
    initialValues: {
      cota: cota || "",
    },
  });

  const { values, setFieldValue } = formik;

  const removeButtonIcon = showRemoveButton ? (
    <S.CloseIcon
      as={FaTimes}
      onClick={onRemoveCota}
      className="cota-remove-icon"
    />
  ) : null;

  const onChangeValue = useCallback(
    (evt) => {
      const valueCota = getPaddingValue(
        evt.currentTarget.value.replace(/\D/gi, "")
      );
      setFieldValue("cota", valueCota);
    },
    [setFieldValue]
  );

  const onChangeHandle = useCallback(() => {
    onCotaChange(values.cota);
  }, [onCotaChange, values.cota]);

  useEffect(() => {
    setFieldValue("cota", cota);
  }, [cota, setFieldValue]);

  return (
    <FormikContext.Provider value={formik}>
      <S.CotaBlock>
        <InputControl
          {...props}
          name="cota"
          onBlur={onChangeHandle}
          onChange={onChangeValue}
        />
        {removeButtonIcon}
      </S.CotaBlock>
    </FormikContext.Provider>
  );
};

InputCotaControl.defaultProps = {
  position: 0,
};

InputCotaControl.propTypes = {
  cota: PropTypes.string.isRequired,
  position: PropTypes.number,
  onCotaChange: PropTypes.func.isRequired,
  onRemoveCota: PropTypes.func.isRequired,
  showRemoveButton: PropTypes.bool.isRequired,
};

const ModalErrorBody = () => {
  const { closeHandle } = useContext(ModalContext);

  return (
    <>
      <Text>
        Verifique se a informações inseridas estão corretas, ou tente novamente
        mais tarde.
      </Text>
      <ButtonPrimaryTransparent label="Fechar" onClick={closeHandle} />
    </>
  );
};

const FormResultadoAssembleia = () => {
  const { isValid, dirty, values, setFieldValue, submitForm } = useContext(
    FormikContext
  );
  const [numeroCotas, setNumeroCotas] = useState(1);

  const permiteIncluirCota = useMemo(
    () => values.cotas.filter((c) => !c).length === 0,
    [values.cotas]
  );

  const addCotaHandle = useCallback(
    (evt) => {
      evt.preventDefault();
      setFieldValue("cotas", values.cotas.concat(""));
    },
    [setFieldValue, values.cotas]
  );

  const optionsMes = useMemo(
    () =>
      getMonths(new Date().getMonth()).map((label, idx) => ({
        label,
        value: idx + 1,
      })),
    []
  );

  const cotaHandleChange = useCallback(
    (value, posicao) => {
      const cotasAtualizadas = values.cotas.map((c, index) =>
        index === posicao ? value : c
      );
      setFieldValue("cotas", cotasAtualizadas);
    },
    [setFieldValue, values.cotas]
  );

  const removeInpuCotaHandle = useCallback(
    (posicao) => {
      const cotasAtualizadas = values.cotas.filter(
        (...[, index]) => index !== posicao
      );
      setFieldValue("cotas", cotasAtualizadas);
    },
    [setFieldValue, values.cotas]
  );

  useEffect(() => {
    setNumeroCotas(values?.cotas?.length);
  }, [values?.cotas?.length]);

  const changeGrupoHandle = useCallback(
    (evt) => {
      const valueGrupo = getPaddingValue(
        evt.currentTarget.value.replace(/\D/gi, "")
      );
      setFieldValue("grupo", valueGrupo);
    },
    [setFieldValue]
  );

  return (
    <>
      <S.GrupoMesContent>
        <S.SelectMes
          as={SelectControl}
          label="Mês"
          placeholder="Selecione..."
          options={optionsMes}
          name="mes"
        />
        <S.InputGrupo
          as={InputControl}
          name="grupo"
          label="Grupo"
          placeholder="Apenas números"
          onChange={changeGrupoHandle}
        />
      </S.GrupoMesContent>
      {Array.from({ length: numeroCotas }, (...[, idx]) => (
        <InputCotaControl
          label="Cota"
          placeholder="Apenas números"
          showRemoveButton={numeroCotas > 1}
          cota={values?.cotas?.[idx]}
          onCotaChange={(cota) => cotaHandleChange(cota, idx)}
          onRemoveCota={() => removeInpuCotaHandle(idx)}
        />
      ))}

      {numeroCotas <= 9 && (
        <Link
          href="#cotas"
          onClick={addCotaHandle}
          disabled={!permiteIncluirCota}
        >
          + Adicionar cota desse grupo ( {10 - values.cotas.length} )
        </Link>
      )}
      <S.ConsultarButton
        onClick={submitForm}
        as={ButtonPrimary}
        disabled={!dirty || !isValid}
      >
        Consultar
      </S.ConsultarButton>
    </>
  );
};

const StatusContemplacaoAssembleia = (props) => {
  const { modalidadesContempladas, cotasContempladas } = { ...props };

  const contemplado = useMemo(
    () => modalidadesContempladas && modalidadesContempladas.length,
    [modalidadesContempladas]
  );

  const modalidadeContemplada = useMemo(
    () =>
      modalidadesContempladas.length === 1
        ? `por ${modalidadesContempladas[0]}`
        : "",
    [modalidadesContempladas]
  );

  const contempladoTitle = useMemo(
    () =>
      cotasContempladas.length > 1
        ? `Parabéns! Suas cotas foram contempladas ${modalidadeContemplada.toLowerCase()} ;)`
        : `Parabéns! A cota ${
            cotasContempladas[0]?.cota
          } foi contemplada ${modalidadeContemplada.toLowerCase()} ;)`,
    [cotasContempladas, modalidadeContemplada]
  );

  const multiplasContemplacoesContent = useMemo(
    () =>
      cotasContempladas.length > 1 ? (
        <S.ContainerCotasSorteadas>
          <S.LabelQuantidadeContemplados
            as={Text}
            textType="caption"
            text={`${cotasContempladas.length} cotas contempladas`}
          />
          <S.ContentCotasSorteadas>
            {applyKey(cotasContempladas, ({ cota }) => (
              <S.CotaSorteada as={Text} elementType="span" text={cota} />
            ))}
          </S.ContentCotasSorteadas>
        </S.ContainerCotasSorteadas>
      ) : null,
    [cotasContempladas]
  );

  return (
    <>
      <StatusContemplacao
        contemplado={contemplado}
        contentNaoContemplado={
          <DoubleContent
            leftContent={
              <>
                <Headline
                  nivel="5"
                  text="Essa cota ainda não foi contemplada :("
                />
                <Text textSize="lg">
                  Continue torcendo e mantenha as parcelas em dia! Quem sabe na
                  próxima assembleia?
                </Text>
              </>
            }
          >
            <Text textType="lead" text="VOCÊ SABIA?" />
            <Text textSize="lg">
              Você pode aumentar suas chances de contemplação ofertando um
              lance! Acesse a{" "}
              <Link
                href={links.consorcio.areaLogada}
                target="_blank"
                label="Área do cliente"
              />{" "}
              e escolha a opção de lance no menu.
            </Text>
          </DoubleContent>
        }
      >
        <div>
          <Headline nivel="5" text={contempladoTitle} />
          <StatusContemplacao.IlustracaoContent>
            <StatusContemplacao.IlustracaoMobile as={IlustracaoMobile} />
          </StatusContemplacao.IlustracaoContent>
          {multiplasContemplacoesContent}
          <S.ButtonContemplacao
            as={ButtonSecondary}
            href={links.consorcio.contemplacao}
            label="Como usar meu crédito"
          />
        </div>
        <StatusContemplacao.IlustracaoContent>
          <StatusContemplacao.IlustracaoDesktop as={IlustracaoDesktop} />
        </StatusContemplacao.IlustracaoContent>
      </StatusContemplacao>
    </>
  );
};

const ResultadoDasAssembleias = () => {
  const {
    data,
    resultados,
    getResult,
    cotas: cotasPesquisadas,
    contemplacao,
    loading,
    error,
    grupo,
    clearRequest,
  } = useResultadoAssembleia();

  const ref = useRef(null);

  const resetFormHandle = useCallback(() => {
    clearRequest();

    animatedScroll(ref.current, {
      verticalOffset: -70,
      easing: (t) => t,
    });
  }, [clearRequest]);

  return (
    <Loading loading={loading}>
      <span ref={ref} />
      {resultados && cotasPesquisadas?.length ? (
        <StatusContemplacaoAssembleia {...contemplacao} />
      ) : null}
      <SectionContent>
        {!Object.keys({ ...data }).length ? (
          <DoubleContent
            leftContent={
              <>
                <Headline nivel="2" styleAs="5">
                  Resultado das assembleias
                </Headline>
                <p>
                  Para saber se hoje é seu dia de sorte, preencha as
                  informações.
                </p>
                <p>Consulte o resultado a partir das 15h.</p>
              </>
            }
            rightContent={
              <ResultadoDasAssembleiasForm
                form={FormResultadoAssembleia}
                getResult={getResult}
              />
            }
          />
        ) : (
          <PainelDeResultados
            resultados={resultados}
            resetForm={resetFormHandle}
          />
        )}

        <ModalError
          error={!loading && error && grupo}
          headerTitle="Não foi possível consultar assembleia :("
        >
          <ModalErrorBody />
        </ModalError>
      </SectionContent>
    </Loading>
  );
};

export default ResultadoDasAssembleias;
